import { ApiSet, BaseResponse, IndexApiSet, useIndexApi, usePostApi, usePutApi } from 'utils/network/api_hooks'
import { Form, useEffectSkipFirst } from 'utils/hooks'
import { CraifHttpClient } from '../utils/network/axios'
import { PagingResponse } from 'entities'
import { Extraction, ExtractionForm, ExtractionSearchForm } from 'entities/extraction'
import { TranscriptionForm } from 'components/shared/type'
import { Equipment } from 'entities/equipment'

type ExtractionsResponse = PagingResponse & {
    results: Extraction[]
}

export function useFetchExtractionsApi(searchForm: Form<ExtractionSearchForm>): IndexApiSet<ExtractionsResponse> & { execute: () => void } {
    const apiPath = 'extractions/'
    const api = useIndexApi<ExtractionsResponse>(new CraifHttpClient(), {
        initialResponse: { count: 0, results: [] },
        initialState: { page: searchForm.object.page || 1, perPage: searchForm.object.perPage || 50 },
    })

    const execute = (): void => {
        api.execute(apiPath, { params: searchForm.object })
    }

    useEffectSkipFirst(() => {
        searchForm.update((f) => {
            if (api.pageSet.page) f.page = api.pageSet.page
            if (api.pageSet.perPage) f.perPage = api.pageSet.perPage
        })
    }, [api.pageSet.page, api.pageSet.perPage])

    useEffectSkipFirst(() => {
        execute()
    }, [searchForm.object])

    return { ...api, execute: execute }
}

type ExtractionResponse = BaseResponse & {
    results: Extraction
}

export function usePostToDnaApi(): ApiSet<ExtractionResponse> & { execute: (ids: Form<TranscriptionForm>) => void } {
    const apiPath = 'extractions/to_dna/'
    const api = usePostApi<ExtractionResponse, TranscriptionForm>(
        new CraifHttpClient(),
        {
            initialResponse: { results: {} },
        },
        { formatJson: true }
    )

    const execute = (form: Form<TranscriptionForm>): void => {
        api.execute(apiPath, form)
    }

    return { ...api, execute: execute }
}

export function usePostExtToBioanalyzerApi(): ApiSet<ExtractionResponse> & { execute: (ids: Form<TranscriptionForm>) => void } {
    const apiPath = 'extractions/to_bioanalyzer/'
    const api = usePostApi<ExtractionResponse, TranscriptionForm>(
        new CraifHttpClient(),
        {
            initialResponse: { results: {} },
        },
        { formatJson: true }
    )

    const execute = (form: Form<TranscriptionForm>): void => {
        api.execute(apiPath, form)
    }

    return { ...api, execute: execute }
}

export function usePostToMicroarrayApi(): ApiSet<ExtractionResponse> & { execute: (ids: Form<TranscriptionForm>) => void } {
    const apiPath = 'extractions/to_microarray/'
    const api = usePostApi<ExtractionResponse, TranscriptionForm>(
        new CraifHttpClient(),
        {
            initialResponse: { results: {} },
        },
        { formatJson: true }
    )

    const execute = (form: Form<TranscriptionForm>): void => {
        api.execute(apiPath, form)
    }

    return { ...api, execute: execute }
}

export function usePutExtractionApi(): ApiSet<BaseResponse> & { execute: (form: Form<ExtractionForm>) => void } {
    const apiSet = usePutApi<BaseResponse, ExtractionForm>(
        new CraifHttpClient(),
        {
            initialResponse: {},
        },
        { formatJson: true }
    )

    const execute = (form: Form<ExtractionForm>) => {
        const apiPath = `extractions/${form.object.id}/`
        apiSet.execute(apiPath, form.object)
    }

    return { ...apiSet, execute: execute }
}

export function useBulkPutExtractionsApi(): ApiSet<BaseResponse> & { execute: (form: Form<ExtractionForm[]>) => void } {
    const apiSet = usePutApi<BaseResponse, ExtractionForm[]>(
        new CraifHttpClient(),
        {
            initialResponse: {},
        },
        { formatJson: true }
    )

    const execute = (form: Form<ExtractionForm[]>) => {
        const apiPath = `extractions/bulk_update/`
        apiSet.execute(apiPath, form.object)
    }

    return { ...apiSet, execute: execute }
}

type CreateProtocolSheetResponse = BaseResponse & {
    key?: string
    equipments?: Equipment[]
}

export type CreateProtocolSheetForm = {
    ids?: string[]
    staffId?: number
    protocol?: 'NW+_Norgen' | 'NW-_Norgen'
}

export function useCreateProtocolSheetApi(): ApiSet<CreateProtocolSheetResponse> & { execute: (form: Form<CreateProtocolSheetForm>) => void } {
    const apiPath = 'extractions/create_protocol_sheet/'
    const api = usePostApi<CreateProtocolSheetResponse, CreateProtocolSheetForm>(
        new CraifHttpClient(),
        {
            initialResponse: {},
        },
        { formatJson: true }
    )

    const execute = (form: Form<CreateProtocolSheetForm>): void => {
        api.execute(apiPath, form)
    }

    return { ...api, execute: execute }
}
